import React, { useEffect, useState } from 'react';
import Store, { TWO_FACTOR_ENROLLMENT_TOKEN_KEY, translateError } from '../store/Store';
import { Page } from '../components/ui/Page'
import QRCode from 'qrcode';
import { Body } from '../components/layout/Body';

type TwoFactorStatus = {
  twoFactorEnabled?: boolean;
  twoFactorVerified?: boolean;
  twoFactorRequired?: boolean;
  enrollmentPending?: boolean;
};

type SetupResponse = {
  secret?: string;
  otpAuthUrl?: string;
};

export default function TwoFactorPage() {
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [setup, setSetup] = useState<SetupResponse | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  // German message why the 2FA setup is not possible (no/invalid enrollment link, rate limit, ...)
  const [setupError, setSetupError] = useState<string | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const twoFactorRequired = Store.use('twoFactorRequired');

  const otpAuthUrl = setup?.otpAuthUrl ?? '';

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!otpAuthUrl) {
        setQrDataUrl('');
        return;
      }

      try {
        const dataUrl = await QRCode.toDataURL(otpAuthUrl, { width: 240, margin: 1 });
        if (!cancelled) setQrDataUrl(dataUrl);
      } catch {
        if (!cancelled) setQrDataUrl('');
      }

    })();
    return () => {
      cancelled = true;
    };
  }, [otpAuthUrl]);

  const refreshStatus = async () => {
    const s = (await Store.fetch('2fa', undefined, false, false)) as TwoFactorStatus | null;
    if (s) {
      setStatus(s);
      Store.set({
        twoFactorRequired: !!s.twoFactorRequired,
        twoFactorVerified: !!s.twoFactorVerified,
      });
    }
    return s;
  };

  // request a new secret - only allowed while 2FA is NOT enabled and a valid enrollment exists
  const requestSetup = async (): Promise<void> => {
    const result = await Store.fetchResult('2fa/setup', { method: 'POST' }, false);
    if (result.ok && result.data) {
      setSetup(result.data as SetupResponse);
      setSetupError(null);
      return;
    }

    setSetup(null);
    if (result.errorCode === '2FA_ALREADY_ENABLED') {
      await refreshStatus();
      return;
    }
    setSetupError(result.errorMessage ?? translateError('2FA_ENROLLMENT_REQUIRED'));
  };

  const startSetupIfNeeded = async (s?: TwoFactorStatus | null) => {
    const st = s ?? status ?? (await refreshStatus());
    if (!st || st.twoFactorEnabled) return; // never call 2fa/setup when 2FA is already active

    let enrollmentValid = !!st.enrollmentPending;

    // token from the e-mail link (#/2fa/enroll?token=...) stored before the login -> redeem it first
    const storedToken = sessionStorage.getItem(TWO_FACTOR_ENROLLMENT_TOKEN_KEY);
    if (storedToken) {
      const enroll = await Store.enrollTwoFactor(storedToken);
      if (enroll.ok) {
        enrollmentValid = true;
      } else if (!enrollmentValid) {
        setSetupError(enroll.errorMessage ?? translateError('2FA_ENROLLMENT_TOKEN_INVALID'));
        return;
      }
    }

    if (!enrollmentValid && st.enrollmentPending === false) {
      setSetupError(translateError('2FA_ENROLLMENT_REQUIRED'));
      return;
    }

    await requestSetup();
  };

  useEffect(() => {
    (async () => {
      const s = await refreshStatus();
      if (s && !s.twoFactorRequired) {
        Store.navigate('/dashboard');
        return;
      }
      await startSetupIfNeeded(s);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onVerify = async () => {
    setBusy(true);
    setVerifyError(null);
    try {
      const result = await Store.fetchResult(
        '2fa/verify',
        { method: 'POST', body: JSON.stringify({ code }) }, false
      );
      const res = result.data;

      if (!result.ok) {
        setVerifyError(result.errorMessage ?? translateError('2FA_INVALID_CODE'));
        return;
      }

      if (res?.twoFactorVerified) {
        Store.set({ twoFactorVerified: true, twoFactorRequired: true });

        const mode = Store.get().mode;

        if (mode === 'PROVIDER' || mode === 'CONSUMER') {
          Store.masterdata = await Store.fetch('masterdata', undefined, true, false);
        }

        Store.navigate('/dashboard');
      } else {
        setVerifyError(translateError('2FA_INVALID_CODE'));
      }
    } finally {
      setBusy(false);
    }
  };

  const onRegenerate = async () => {
    if (status?.twoFactorEnabled) return;
    setBusy(true);
    try {
      await requestSetup();
    } finally {
      setBusy(false);
    }
  };

  const enabled = !!status?.twoFactorEnabled;
  const required = twoFactorRequired || !!status?.twoFactorRequired;
  const canVerify = enabled || !!setup?.otpAuthUrl;

  return (
    <Page id="twofactor" className="content-center">
      <Body id="twofactor-body">
        <h1 className="home-page-header">2-Faktor-Authentifizierung</h1>

        {required && !enabled && setupError && (
          <div className="mt-6 max-w-xl bg-red-100 text-red-700 p-3 rounded" data-pw="twofactor-setup-error">
            {setupError}
          </div>
        )}

        {required && !enabled && !setupError && setup && (
          <div className="mt-6">

            <div className="mt-6">
              <h2 className="mb-2 subtitle-homepage">2FA aktivieren</h2>
            </div>

            {qrDataUrl && (
              <div className="mb-3">
                <img src={qrDataUrl} alt="2FA QR code" style={{ width: 240, height: 240 }} />
              </div>
            )}

            {setup?.secret && (
              <div className="mb-3">
                <div style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{setup.secret}</div>
              </div>
            )}

            {setup?.otpAuthUrl && (
              <div className="mb-3">
                <div style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{setup.otpAuthUrl}</div>
              </div>
            )}

            {/* only while 2FA is not active - an active secret is never delivered again */}
            {!enabled && (
              <button className="bg-gray-300 text-black px-4 py-2 rounded-md" onClick={onRegenerate} disabled={busy}>
                Neues Secret generieren
              </button>
            )}
          </div>
        )}

        {required && enabled && (
          <div className="mt-6">
            <h2 className="mb-2 subtitle-homepage">Code eingeben</h2>
          </div>
        )}

        {canVerify && (
          <div className="mt-6">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="000000"
              inputMode="numeric"
              autoComplete="one-time-code"
              className="w-full max-w-xs rounded-[20px] border border-[#333] px-3 py-2 text-area mb-20"
            />
            {verifyError && (
              <div className="text-red-700 mb-3" data-pw="twofactor-verify-error">{verifyError}</div>
            )}
            <div className="mt-3">
              <button className="bg-gray-300 text-black px-4 py-2 rounded-md"
                      onClick={onVerify} disabled={busy || code.trim().length < 6}>
                Verifizieren
              </button>
            </div>
          </div>
        )}
      </Body>
    </Page>
  );
}
